import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";

export default function TournamentFightSchedule() {
  const { id } = useParams();

  const [schedule, setSchedule] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeRing, setActiveRing] = useState(1);
  const [error, setError] = useState("");

  // ==========================
  // LOAD TOURNAMENT
  // ==========================
  async function loadTournament() {
    try {
      const data = await API.get(`/tournaments/${id}`);
      setTournament(data);
    } catch (err) {
      console.error("Failed to load tournament:", err);
    }
  }

  // ==========================
// LOAD FIGHT SCHEDULE
// ==========================
async function loadSchedule() {
  try {
    setLoading(true);
    setError("");

    const data = await API.get(
      `/tournaments/${id}/fight-schedule`
    );

    console.log("Fight schedule:", data);

    if (data?.rings && typeof data.rings === "object") {

      const allMatches = Object.values(data.rings)
        .flat()
        .sort((a, b) => {
          const ringDiff =
            Number(a.ring_number || 0) -
            Number(b.ring_number || 0);

          if (ringDiff !== 0) return ringDiff;

          return (
            Number(a.fight_number || 999999) -
            Number(b.fight_number || 999999)
          );
        });

      setSchedule(allMatches);

      const availableRings = Object.keys(data.rings)
        .map(Number)
        .filter(Boolean)
        .sort((a, b) => a - b);

      if (availableRings.length > 0) {
        setActiveRing((currentRing) =>
          availableRings.includes(Number(currentRing))
            ? Number(currentRing)
            : availableRings[0]
        );
      }

    } else if (Array.isArray(data)) {

      setSchedule(data);

    } else {

      setSchedule(
        data?.matches ||
        data?.schedule ||
        []
      );

    }

  } catch (err) {

    console.error(
      "Failed to load fight schedule:",
      err
    );

    setError(
      err.message ||
      "Failed to load fight schedule."
    );

    setSchedule([]);

  } finally {

    setLoading(false);

  }
}

  // ==========================
  // INITIAL LOAD
  // ==========================
  useEffect(() => {
    loadTournament();
    loadSchedule();
  }, [id]);

  // ==========================
  // GENERATE SCHEDULE
  // ==========================
  async function generateSchedule() {
    const confirmed = window.confirm(
      "Generate or regenerate the fight schedule?\n\nExisting fight numbers may be replaced."
    );

    if (!confirmed) return;

    try {
      setGenerating(true);
      setError("");

      const result = await API.post(
        `/tournaments/${id}/generate-fight-schedule`,
        {}
      );

      console.log(
        "Schedule generation result:",
        result
      );

      await loadSchedule();

      alert("Fight schedule generated successfully.");

    } catch (err) {
      console.error(
        "Fight schedule generation failed:",
        err
      );

      alert(
        err.message ||
        "Failed to generate fight schedule."
      );

    } finally {
      setGenerating(false);
    }
  }

  // ==========================
// FILTER BY RING
// ==========================
const rings = [
  ...new Set(
    schedule
      .map((match) => Number(match.ring_number))
      .filter(Boolean)
  )
].sort((a, b) => a - b);


// Only REAL scheduled fights
const ringMatches = schedule
  .filter(
    (match) =>
      Number(match.ring_number) === Number(activeRing) &&
      Number(match.fight_number) > 0
  )
  .sort(
    (a, b) =>
      Number(a.fight_number) -
      Number(b.fight_number)
  );

  // ==========================
// STATS
// ==========================

// Only matches that actually have a fight number
const scheduledMatches = schedule.filter(
  (match) =>
    Number(match.fight_number) > 0 &&
    Number(match.ring_number) > 0
);

const totalScheduled = scheduledMatches.length;


// Unscheduled / invalid records
const unscheduledMatches = schedule.filter(
  (match) =>
    !match.fight_number ||
    Number(match.fight_number) <= 0
);


// Completed scheduled fights
const completedMatches = scheduledMatches.filter(
  (match) => match.status === "completed"
).length;


// Remaining fights
const pendingMatches =
  totalScheduled - completedMatches;

  // ==========================
// FIGHT DISPLAY HELPERS
// ==========================

function getRedDisplay(match) {
  if (match.red_fighter_name) {
    return match.red_fighter_name;
  }

  if (match.red_source_fight) {
    return `Winner of Fight #${match.red_source_fight}`;
  }

  return "TBD";
}

function getBlueDisplay(match) {
  if (match.blue_fighter_name) {
    return match.blue_fighter_name;
  }

  if (match.blue_source_fight) {
    return `Winner of Fight #${match.blue_source_fight}`;
  }

  return "TBD";
}

function getClubLogo(logo) {
  if (!logo) return null;

  return `https://warfc.com/api/uploads/clubs/${logo}`;
}

const isRealScheduledFight = (match) => {
  return (
    Number(match.fight_number) > 0 &&
    Number(match.ring_number) > 0
  );
};

const hasBothSides = (match) => {
  const redExists =
    match.red_fighter_name ||
    match.red_fighter ||
    match.source_fights?.red;

  const blueExists =
    match.blue_fighter_name ||
    match.blue_fighter ||
    match.source_fights?.blue;

  return redExists && blueExists;
};

function isWinner(match, fighterId) {
  return (
    match.status === "completed" &&
    Number(match.winner) === Number(fighterId)
  );
}

  // ==========================
  // RENDER
  // ==========================
  return (
    <AdminLayout>
      <div className="p-6 text-white space-y-6">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

          <div>
            <Link
              to={`/admin/tournament/${id}`}
              className="text-gray-400 hover:text-white text-sm"
            >
              ← Back to Tournament
            </Link>

            <h1 className="text-3xl font-bold mt-2">
              🗓️ Fight Schedule
            </h1>

            {tournament && (
              <p className="text-gray-400 mt-1">
                {tournament.name}
              </p>
            )}
          </div>

          <button
            onClick={generateSchedule}
            disabled={generating}
            className="
              px-5 py-3
              bg-yellow-500
              hover:bg-yellow-400
              disabled:bg-gray-600
              disabled:cursor-not-allowed
              text-black
              font-bold
              rounded-xl
              transition
            "
          >
            {generating
              ? "Generating..."
              : "⚡ Generate Schedule"}
          </button>

        </div>

        {/* STATS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
            <div className="text-gray-400 text-sm">
              Total Scheduled
            </div>

            <div className="text-3xl font-bold mt-1">
              {totalScheduled}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
            <div className="text-gray-400 text-sm">
              Pending
            </div>

            <div className="text-3xl font-bold text-yellow-400 mt-1">
              {pendingMatches}
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-xl p-4">
            <div className="text-gray-400 text-sm">
              Completed
            </div>

            <div className="text-3xl font-bold text-green-400 mt-1">
              {completedMatches}
            </div>
          </div>

        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-900/40 border border-red-500 text-red-300 rounded-xl p-4">
            {error}
          </div>
        )}

        {/* LOADING */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">
            Loading fight schedule...
          </div>
        ) : schedule.length === 0 ? (

          /* EMPTY STATE */
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-10 text-center">

            <div className="text-5xl mb-4">
              🥊
            </div>

            <h2 className="text-xl font-bold">
              No Fight Schedule Yet
            </h2>

            <p className="text-gray-400 mt-2">
              Generate the tournament fight schedule to begin.
            </p>

          </div>

        ) : (

          <>
            {/* RING TABS */}
            <div className="flex flex-wrap gap-3">

              {rings.map((ring) => (
                <button
                  key={ring}
                  onClick={() => setActiveRing(ring)}
                  className={`
                    px-6 py-3
                    rounded-xl
                    font-bold
                    transition
                    ${
                      Number(activeRing) === Number(ring)
                        ? "bg-yellow-500 text-black"
                        : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    }
                  `}
                >
                  🥊 Ring {ring}

                  <span className="ml-2 opacity-70">
                    {
                      schedule.filter(
                        (m) =>
                          Number(m.ring_number) === Number(ring)
                      ).length
                    }
                  </span>

                </button>
              ))}

            </div>

            {/* SCHEDULE TABLE */}
            <div className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden">

              <div className="overflow-x-auto">

                <table className="w-full text-sm">

                  <thead className="bg-gray-800 text-gray-300">

                    <tr>

                      <th className="text-left p-4">
                        Fight
                      </th>

                      <th className="text-left p-4">
                        Red Corner
                      </th>

                      <th className="text-center p-4">
                        VS
                      </th>

                      <th className="text-left p-4">
                        Blue Corner
                      </th>

                      <th className="text-left p-4">
                        Division
                      </th>

                      <th className="text-left p-4">
                        Round
                      </th>

                      <th className="text-left p-4">
                        Status
                      </th>

                    </tr>

                  </thead>

                  <tbody>

                    {ringMatches.map((match) => (

                      <tr
                        key={match.id}
                        className="
                          border-t
                          border-gray-800
                          hover:bg-gray-800/60
                          transition
                        "
                      >

                        {/* FIGHT NUMBER */}
                        <td className="p-4">

                          <span className="font-bold text-yellow-400">
                            #{match.fight_number}
                          </span>

                        </td>

      {/* RED */}
<td className="p-4">
  {match.red_fighter_name ? (
    <div>
      <div className="flex items-center flex-wrap gap-2">
        <span className="font-semibold text-red-400">
          {match.red_fighter_name}
        </span>
      </div>

      {match.red_club_name && (
        <div className="flex items-center gap-2 mt-1">
          {match.red_club_logo && (
            <img
              src={`https://warfc.com/api/uploads/clubs/${match.red_club_logo}`}
              alt={match.red_club_name}
              className="w-6 h-6 object-contain"
            />
          )}

          <span className="text-xs text-gray-400">
            {match.red_club_name}
          </span>
        </div>
      )}
    </div>
  ) : match.source_fights?.red ? (
    <div className="flex items-center gap-2">
      <span className="text-orange-400 font-semibold">
        🏆 Winner of Fight #{match.source_fights.red}
      </span>
    </div>
  ) : (
    <span className="text-gray-500">TBD</span>
  )}
</td>

{/* VS */}
<td className="p-4 text-center">
  {match.status === "completed" ? (
    <span className="text-green-400 font-bold text-xs">
      ✓ DONE
    </span>
  ) : (
    <span className="text-gray-500 font-bold">
      VS
    </span>
  )}
</td>

{/* BLUE */}
<td className="p-4">
  {match.blue_fighter_name ? (
    <div>
      <div className="flex items-center flex-wrap gap-2">
        <span className="font-semibold text-blue-400">
          {match.blue_fighter_name}
        </span>
      </div>

      {match.blue_club_name && (
        <div className="flex items-center gap-2 mt-1">
          {match.blue_club_logo && (
            <img
              src={`https://warfc.com/api/uploads/clubs/${match.blue_club_logo}`}
              alt={match.blue_club_name}
              className="w-6 h-6 object-contain"
            />
          )}

          <span className="text-xs text-gray-400">
            {match.blue_club_name}
          </span>
        </div>
      )}
    </div>
  ) : match.source_fights?.blue ? (
    <div className="flex items-center gap-2">
      <span className="text-orange-400 font-semibold">
        🏆 Winner of Fight #{match.source_fights.blue}
      </span>
    </div>
  ) : (
    <span className="text-gray-500">TBD</span>
  )}
</td>

                        {/* DIVISION */}
                        <td className="p-4">

                          <div className="font-medium">
                            {match.age_division || "-"}
                          </div>

                          <div className="text-gray-400 text-xs">
                            {match.weightclass_name || "-"}
                          </div>

                        </td>

                        {/* ROUND */}
                        <td className="p-4">
                          Round {match.round}
                        </td>

                        {/* STATUS */}
                        <td className="p-4">

                          <span
                            className={`
                              px-3 py-1
                              rounded-full
                              text-xs
                              font-semibold
                              ${
                                (match.status || "scheduled") === "completed"
                                  ? "bg-green-500/20 text-green-400"
                                  : "bg-yellow-500/20 text-yellow-400"
                              }
                            `}
                          >
                            {match.status || "scheduled"}
                          </span>

                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            </div>

          </>
        )}

      </div>
    </AdminLayout>
  );
}